import { Suspense, lazy, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet';
import MultiStageSearch from '../../components/MultiStageSearch';
import { useQuery } from '@tanstack/react-query';
import { graphql } from '../../gql/gql';
import { AllFeatureTripQueryQuery } from '../../gql/graphql';
const FeatureTrip = lazy(() => import('./components/FeatureTrip'));
const TopDiveDestinations = lazy(
  () => import('./components/TopDiveDestinations'),
);
const DrawerMenu = lazy(() => import('./components/DrawerMenu'));
const SearchOverlay = lazy(() => import('../../components/SearchOverlay'));
import { IconChevronsDown, IconMenu2, IconMouse } from '@tabler/icons-react';
import { useAuth0 } from '../../auth/AuthProvider';

import { homeInitialState, homeReducer } from '../../reducers/homeReducer';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';

const DrawerMenuSkeleton = () => (
  <div className="fixed inset-y-0 right-0 w-64 bg-gray-100 animate-pulse" />
);

const FeatureTripSkeleton = () => (
  <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-12">
    <div className="mb-6 h-8 w-1/4 animate-pulse rounded bg-gray-200" />
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="h-64 animate-pulse rounded-2xl bg-gray-200" />
      ))}
    </div>
  </div>
);

const TopDestinationsSkeleton = () => (
  <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-12">
    <div className="mb-6 h-8 w-1/3 animate-pulse rounded bg-gray-200" />
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="h-64 animate-pulse rounded-2xl bg-gray-200" />
      ))}
    </div>
  </div>
);

const SearchOverlaySkeleton = () => (
  <div className="fixed inset-0 bg-gray-100 bg-opacity-50 animate-pulse" />
);

const LOGO_URL =
  'https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/916bc200-cd79-4632-1c5f-c93e820d3c00/public';

const Home = () => {
  const [isShowDrawer, setIsShowDrawer] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [state, dispatch] = useReducer(homeReducer, homeInitialState);
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const allFeatureTripQuery = graphql(/* GraphQL */ `
    query allFeatureTripQuery {
      trip {
        tripId
        routeId
        embarkationDate
        disembarkationDate
        numberOfDays
        numberOfNights
        diveCount
        price
        imageUrl
        currency
        organizerId
        routeName
        boatName
        boatId
      }
    }
  `);

  const { data, isLoading } = useQuery<AllFeatureTripQueryQuery>({
    queryKey: ['trips'],
    queryFn: async () => {
      const { graphqlRequest } = await import('../../utils/graphqlClient');
      return graphqlRequest<AllFeatureTripQueryQuery>(allFeatureTripQuery); // Public endpoint, no auth needed
    },
  });

  const handleLogin = () => {
    localStorage.setItem(
      'redirectAfterLogin',
      window.location.pathname + window.location.search,
    );
    navigate('/login');
  };

  return (
    <>
      <Helmet>
        <title>
          Liveaboard Diving Trips — Thailand, Indonesia &amp; More | Santtai
        </title>
        <meta
          name="description"
          content="Book liveaboard diving trips across Thailand, Indonesia, Maldives &amp; more. Compare boats, itineraries and prices. Book now, pay later."
        />
        <link rel="canonical" href="https://www.santtai.com/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Liveaboard Diving Trips — Thailand, Indonesia & More | Santtai"
        />
        <meta
          property="og:description"
          content="Book liveaboard diving trips across Thailand, Indonesia, Maldives & more. Compare boats, itineraries and prices. Book now, pay later."
        />
        <meta property="og:url" content="https://www.santtai.com/" />
        <meta
          property="og:image"
          content="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/e688aa15-3a85-4e4b-5925-4526ffeeda00/public"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Liveaboard Diving Trips — Thailand, Indonesia & More | Santtai"
        />
        <meta
          name="twitter:description"
          content="Book liveaboard diving trips across Thailand, Indonesia, Maldives & more. Compare boats, itineraries and prices. Book now, pay later."
        />
        <meta
          name="twitter:image"
          content="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/e688aa15-3a85-4e4b-5925-4526ffeeda00/public"
        />
      </Helmet>
      <div className="relative flex w-full min-h-[32rem] h-[70vh] max-h-[44rem] flex-col justify-between bg-hero bg-center bg-no-repeat bg-cover rounded-b-3xl lg:h-screen lg:max-h-none lg:rounded-none">
        <header className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-6 text-white sm:px-6 lg:px-12">
          <Link to="/" className="hidden lg:block" aria-label="Santtai home">
            <img src={LOGO_URL} width={140} height={40} alt="Santtai" />
          </Link>
          <nav
            className="hidden items-center gap-8 text-sm font-semibold lg:flex"
            aria-label="Primary"
          >
            <a
              href="#featured-trips"
              className="text-white no-underline hover:opacity-80"
            >
              Featured trips
            </a>
            <a
              href="#destinations"
              className="text-white no-underline hover:opacity-80"
            >
              Destinations
            </a>
            {isAuthenticated ? (
              <>
                <Link
                  to="/app/tripmanagement"
                  className="text-white no-underline hover:opacity-80"
                >
                  My trips
                </Link>
                <Link
                  to="/profile"
                  className="text-white no-underline hover:opacity-80"
                >
                  Profile
                </Link>
              </>
            ) : (
              <button
                type="button"
                onClick={handleLogin}
                className="rounded-lg border border-white/40 px-4 py-2 text-white hover:bg-white/10"
              >
                Login
              </button>
            )}
          </nav>
          <button
            className="flex min-h-11 min-w-11 items-center justify-center lg:hidden"
            onClick={() => setIsShowDrawer(true)}
            type="button"
            aria-label="Open menu"
            aria-expanded={isShowDrawer}
          >
            <IconMenu2 />
          </button>
          <Link to="/" className="block lg:hidden" aria-label="Santtai home">
            <img src={LOGO_URL} width={120} height={34} alt="Santtai" />
          </Link>
        </header>
        <div className="flex flex-1 flex-col items-center justify-center px-4 pb-8 lg:pb-0">
          <h1 className="text-center font-martel text-3xl font-bold text-white lg:text-5xl">
            dive right in
          </h1>
          <p className="mt-3 max-w-xl text-center text-sm text-white/90 lg:mt-4 lg:text-lg">
            Book liveaboard diving trips across Thailand, Indonesia &amp;
            beyond.
          </p>
          <MultiStageSearch
            setShowSearchOverlay={setShowSearchOverlay}
            destination={state.destination}
            month={state.month}
            diver={state.diver}
            currentYear={state.currentYear}
            currentStage={state.currentStage}
            destinationId={state.destinationId}
            monthId={state.monthId}
            searchType={state.searchType}
            dispatch={dispatch}
          />
        </div>
        <div
          className="mb-12 hidden flex-col items-center text-sm text-neutral-00 lg:flex"
          aria-hidden="true"
        >
          <div className="animate-bounce">
            <IconMouse size={32} />
          </div>
          <div>Explore more</div>
          <div>
            <IconChevronsDown size={32} />
          </div>
        </div>
      </div>
      <Suspense fallback={<DrawerMenuSkeleton />}>
        <DrawerMenu
          isShowDrawer={isShowDrawer}
          setIsShowDrawer={setIsShowDrawer}
        />
      </Suspense>
      {isLoading ? (
        <FeatureTripSkeleton />
      ) : (
        <Suspense fallback={<FeatureTripSkeleton />}>
          {data && <FeatureTrip data={data} />}
        </Suspense>
      )}
      <Suspense fallback={<TopDestinationsSkeleton />}>
        <TopDiveDestinations />
      </Suspense>
      <Suspense fallback={<SearchOverlaySkeleton />}>
        <SearchOverlay
          showSearchOverlay={showSearchOverlay}
          setShowSearchOverlay={setShowSearchOverlay}
          dispatch={dispatch}
          destination={state.destination}
          month={state.month}
          diver={state.diver}
          currentYear={state.currentYear}
          searchType={state.searchType}
          destinationId={state.destinationId}
          monthId={state.monthId}
        />
      </Suspense>
      <Footer />
    </>
  );
};

export default Home;
